import React from 'react';
import PropTypes from 'prop-types';
import { LuInfo } from 'react-icons/lu';

import { BREAKDOWN_TABS, ORIGIN_COVERAGE_START } from '../constants';
import {
   formatCurrency,
   formatIsoDate,
   formatNumber,
   formatPercent,
   pluralize,
} from '../utils/format';
import {
   CardHead,
   CardSubtitle,
   CardTitle,
   DataTable,
   EmptyText,
   InfoStrip,
   MutedRow,
   OverdueCell,
   Refetching,
   RowSub,
   Segmented,
   SegmentedButton,
   ShareCell,
   ShareFill,
   SharePct,
   ShareTrack,
   TableFooter,
   TableWrap,
} from '../styles';
import { blockShape } from '../utils/shapes';
import { BlockErrorState, BlockSkeleton } from './BlockState';

// ---------------------------------------------------------------------------
// Dobra 4 · Onde nasce e onde concentra.
//
// Três recortes do mesmo emitido: departamento gerador, CNPJ emissor e cliente.
// Trocar de aba recarrega SÓ este bloco (`by`), não os outros quatro.
//
// 🔑 `sem_classificacao` PRECISA aparecer. Em `by=origin` na base inteira são
// 4.841 de 7.019 títulos (71,1% do valor): omitir mente por omissão, somar ao
// top N mente por invenção. Ele e o `outros` vão sempre no fim, sempre em
// cinza — não são categorias, são o resto.
//
// ⚠️ Ambos podem vir `null`, inclusive com o filtro `none` ativo (handoff 0086
// §2.4: "não há nada nesse recorte"). Nada de acessar `.titulos` direto.
//
// Sem drill-down: a tabela não navega para a listagem — a linha tem realce de
// leitura, mas não cursor de clique (ver 0086 §5).
// ---------------------------------------------------------------------------

const KEY_HEADERS = {
   origin: 'Departamento origem',
   bank_account: 'Conta recebedora',
   customer: 'Cliente',
};

const FOOTERS = {
   origin: `A quebra por departamento só é confiável a partir de ${formatIsoDate(ORIGIN_COVERAGE_START)}: a carga histórica do ASAAS entrou sem origem.`,
   bank_account:
      'Uma linha por CNPJ emissor. Títulos anteriores ao cadastro das contas podem não ter conta vinculada.',
   customer:
      'Agrupado por CPF/CNPJ normalizado — documentos com máscara são resolvidos pelo backend. O rótulo é o nome mais recente do cliente.',
};

// Cinza para o que não é categoria: "Outros" um pouco mais escuro que a carga
// histórica, para os dois não se confundirem entre si.
const OTHERS_BAR = '#9aa4ad';
const UNCLASSIFIED_BAR = '#b8b8b8';

function MoneyCell({ value = null }) {
   return (
      <td>
         {value === null || value === undefined ? '—' : formatCurrency(value)}
      </td>
   );
}

// eslint-disable-next-line react/require-default-props
MoneyCell.propTypes = { value: PropTypes.number };

function ShareBar({ pct, maxPct, color = null }) {
   const width = maxPct > 0 ? Math.max(0, (pct / maxPct) * 100) : 0;
   return (
      <ShareCell>
         <ShareTrack>
            <ShareFill style={{ width: `${width}%` }} $color={color} />
         </ShareTrack>
         <SharePct>{formatPercent(pct, 1)}</SharePct>
      </ShareCell>
   );
}

ShareBar.propTypes = {
   pct: PropTypes.number.isRequired,
   maxPct: PropTypes.number.isRequired,
   // eslint-disable-next-line react/require-default-props
   color: PropTypes.string,
};

export default function BreakdownPanel({
   block,
   by,
   onChangeBy,
   bankAccountsCount,
   onRetry,
}) {
   const { data } = block;
   const rows = data?.rows ?? [];
   const outros = data?.outros ?? null;
   const unclassified = data?.sem_classificacao ?? null;

   const maxPct = Math.max(
      0,
      ...rows.map((row) => row.share_pct ?? 0),
      outros?.share_pct ?? 0,
      unclassified?.share_pct ?? 0,
   );

   const hasAnyRow = rows.length > 0 || Boolean(outros) || Boolean(unclassified);

   const renderBody = () => {
      if (block.loading && !data) return <BlockSkeleton rows={4} height={44} />;
      if (block.error) {
         return <BlockErrorState error={block.error} onRetry={onRetry} />;
      }
      if (!hasAnyRow) {
         return (
            <EmptyText>
               Nenhum título no recorte para esta quebra. Meses sem títulos são
               zero real, não falha de coleta.
            </EmptyText>
         );
      }

      return (
         <TableWrap>
            <DataTable>
               <thead>
                  <tr>
                     <th>{KEY_HEADERS[by]}</th>
                     <th>Títulos</th>
                     <th>Emitido</th>
                     <th>Recebido</th>
                     <th>Em aberto</th>
                     <th>Vencido</th>
                     <th>Share do emitido</th>
                  </tr>
               </thead>
               <tbody>
                  {rows.map((row) => (
                     <tr key={row.key}>
                        <td>{row.label}</td>
                        <td>{formatNumber(row.titulos)}</td>
                        <MoneyCell value={row.emitido} />
                        <MoneyCell value={row.recebido} />
                        <MoneyCell value={row.em_aberto} />
                        <td>
                           {/* Zero aqui é medido e se exibe como moeda; ausente
                               vira traço. Um `?? 0` transformaria "não sei" em
                               "está limpo", que é o erro mais caro desta tela. */}
                           <OverdueCell $hasValue={Boolean(row.vencido)}>
                              {row.vencido === null || row.vencido === undefined
                                 ? '—'
                                 : formatCurrency(row.vencido)}
                           </OverdueCell>
                        </td>
                        <td>
                           <ShareBar pct={row.share_pct ?? 0} maxPct={maxPct} />
                        </td>
                     </tr>
                  ))}

                  {outros ? (
                     <MutedRow>
                        <td>
                           Outros
                           <RowSub>
                              {pluralize(outros.grupos ?? 0, 'grupo', 'grupos')}
                           </RowSub>
                        </td>
                        <td>{formatNumber(outros.titulos)}</td>
                        <MoneyCell value={outros.emitido} />
                        <MoneyCell value={null} />
                        <MoneyCell value={null} />
                        <td>
                           <OverdueCell $hasValue={false}>—</OverdueCell>
                        </td>
                        <td>
                           <ShareBar
                              pct={outros.share_pct ?? 0}
                              maxPct={maxPct}
                              color={OTHERS_BAR}
                           />
                        </td>
                     </MutedRow>
                  ) : null}

                  {unclassified ? (
                     <MutedRow>
                        <td>
                           Sem classificação
                           <RowSub>carga histórica</RowSub>
                        </td>
                        <td>{formatNumber(unclassified.titulos)}</td>
                        <MoneyCell value={unclassified.emitido} />
                        <MoneyCell value={null} />
                        <MoneyCell value={null} />
                        <td>
                           <OverdueCell $hasValue={false}>—</OverdueCell>
                        </td>
                        <td>
                           <ShareBar
                              pct={unclassified.share_pct ?? 0}
                              maxPct={maxPct}
                              color={UNCLASSIFIED_BAR}
                           />
                        </td>
                     </MutedRow>
                  ) : null}
               </tbody>
            </DataTable>
         </TableWrap>
      );
   };

   return (
      <>
         <CardHead>
            <div>
               <CardTitle as="h3">Onde nasce e onde concentra</CardTitle>
               <CardSubtitle>
                  {data?.total?.titulos
                     ? `${formatNumber(data.total.titulos)} títulos · ${formatCurrency(data.total.emitido)} emitidos no recorte`
                     : 'Quebra do emitido no recorte'}
               </CardSubtitle>
            </div>
            <Segmented role="group" aria-label="Quebra por">
               {BREAKDOWN_TABS.map((tab) => (
                  <SegmentedButton
                     key={tab.key}
                     type="button"
                     $active={by === tab.key}
                     aria-pressed={by === tab.key}
                     onClick={() => onChangeBy(tab.key)}
                  >
                     {tab.label}
                  </SegmentedButton>
               ))}
            </Segmented>
         </CardHead>

         {/* `=== 1` e não `<= 1`: com contagem 0 não sabemos quantas contas
             existem (o catálogo pode ter falhado, ou o usuário não tem
             `finance:bank_account:read`), e afirmar "há uma única conta" seria
             inventar. */}
         {by === 'bank_account' && bankAccountsCount === 1 ? (
            <InfoStrip>
               <LuInfo size={16} strokeWidth={1.8} aria-hidden="true" />
               <span>
                  Há uma única conta recebedora cadastrada. O filtro por CNPJ
                  emissor fica oculto até o 2º CNPJ entrar.
               </span>
            </InfoStrip>
         ) : null}

         <Refetching $busy={block.loading && Boolean(data)}>
            {renderBody()}
         </Refetching>

         <TableFooter>{FOOTERS[by]}</TableFooter>
      </>
   );
}

BreakdownPanel.propTypes = {
   block: blockShape(
      PropTypes.shape({
         by: PropTypes.string,
         total: PropTypes.shape({
            titulos: PropTypes.number,
            emitido: PropTypes.number,
         }),
         rows: PropTypes.arrayOf(PropTypes.shape({})),
         outros: PropTypes.shape({
            grupos: PropTypes.number,
            titulos: PropTypes.number,
            emitido: PropTypes.number,
            share_pct: PropTypes.number,
         }),
         sem_classificacao: PropTypes.shape({
            titulos: PropTypes.number,
            emitido: PropTypes.number,
            share_pct: PropTypes.number,
         }),
      }),
   ).isRequired,
   by: PropTypes.string.isRequired,
   onChangeBy: PropTypes.func.isRequired,
   bankAccountsCount: PropTypes.number.isRequired,
   onRetry: PropTypes.func.isRequired,
};
